import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { fetchJobs, jobSchema } from "@/api/sync";
import { useConnectionTestMutation } from "@/api/settings";

/**
 * Baglanti testi is takibi — /api/v2/tenant/connection-test + /api/v2/jobs
 * (gorev talimati madde 3). `POST /connection-test` `job_id:null` DONEBILIR
 * (ayni tenant icin zaten kuyrukta/calisan bir test varsa — dedupe_key,
 * jobs.py `enqueue()`); bu durumda `job_id` ARAMAK yerine `GET /jobs`
 * listesindeki EN SON `connection_test` isini TAKIP ederiz (backend'de
 * `GET /jobs/{id}` YOK — sozlesme §5).
 */

export const connectionCheckResultSchema = z.object({
  ok: z.boolean(),
  platform: z.string().nullable(),
  checks: z.record(z.string(), z.unknown()),
  message: z.string().nullable(),
});
export type ConnectionCheckResult = z.infer<typeof connectionCheckResultSchema>;

const connectionTestJobSchema = jobSchema.extend({
  result: connectionCheckResultSchema.nullable().optional(),
  // Backend sutun adi `last_error`dur (core.jobs.last_error, db/init/100_core_schema.sql) —
  // burada `error` YOKTU, her zaman undefined kaliyordu (inceleme bulgusu).
  last_error: z.string().nullable().optional(),
});
export type ConnectionTestJob = z.infer<typeof connectionTestJobSchema>;

const TERMINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

/** Test amacli disaridan degistirilebilir modul seviyesi sabit — varsayilan
 * uretimde 3sn, testlerde ~10ms'e dusurulur (advisor onerisi: sahte
 * zamanlayici yerine gercek kisa interval). */
export const POLL_INTERVALS = { ms: 3000 };

export function useConnectionTestStatus() {
  const testMutation = useConnectionTestMutation();
  const [polling, setPolling] = useState(false);
  const [knownJobId, setKnownJobId] = useState<number | null>(null);

  const jobsQuery = useQuery({
    queryKey: ["kurulum", "connection-test-jobs"],
    queryFn: () => fetchJobs(20),
    enabled: polling,
    refetchInterval: polling ? POLL_INTERVALS.ms : false,
  });

  const jobs: ConnectionTestJob[] = (jobsQuery.data?.jobs ?? [])
    .filter((j) => j.kind === "connection_test")
    .map((j) => connectionTestJobSchema.parse(j));

  const activeJob = knownJobId != null ? jobs.find((j) => j.id === knownJobId) : jobs[0];

  useEffect(() => {
    if (polling && activeJob && TERMINAL_STATUSES.has(activeJob.status)) {
      setPolling(false);
    }
  }, [polling, activeJob]);

  async function runTest() {
    setPolling(false);
    try {
      const res = await testMutation.mutateAsync();
      // job_id null ise (dedupe) bilinen id SIFIRLANIR — jobs[0] (en son
      // connection_test) TAKIP EDILIR; queued/running bir is zaten var demektir.
      setKnownJobId(res.job_id ?? null);
      setPolling(true);
    } catch {
      // testMutation.isError zaten UI'a yansir — burada yeniden firlatmaya
      // GEREK YOK (cagiran fire-and-forget cagirir, islenmemis red onlenir).
    }
  }

  return {
    runTest,
    isStarting: testMutation.isPending,
    startError: testMutation.isError,
    isPolling: polling,
    isFetching: jobsQuery.isFetching,
    job: activeJob ?? null,
  };
}
